import type { ZigZagSettings } from "../../model";
import type { ZoomRange, CandleWithFlag } from "features/chart";
import { GRID, CANDLE_MIN_WIDTH } from "features/chart";

export function drawZigZag(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  data: CandleWithFlag[],
  zoom: ZoomRange,
  values: (number | null)[],
  settings: ZigZagSettings,
): void {
  if (!settings.mainLine.visible) return;

  const { left, right, top, bottom } = GRID;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const { start, end } = zoom;
  const startIdx = Math.floor(start);
  const endIdx = Math.min(Math.floor(end), data.length - 1);
  const visibleCount = Math.floor(end) - startIdx + 1;
  const cw = Math.max(CANDLE_MIN_WIDTH, chartWidth / visibleCount);

  let minPrice = Infinity, maxPrice = -Infinity;
  for (let i = startIdx; i <= endIdx; i++) {
    const c = data[i]; if (!c) continue;
    minPrice = Math.min(minPrice, c.low);
    maxPrice = Math.max(maxPrice, c.high);
  }
  if (minPrice === Infinity) return;
  const priceRange = maxPrice - minPrice || 1;
  minPrice -= priceRange * 0.05;
  maxPrice += priceRange * 0.05;
  const range = maxPrice - minPrice;
  const priceToY = (p: number) => top + ((maxPrice - p) / range) * chartHeight;
  const getX = (i: number) => left + (i - startIdx + 0.5) * cw;

  ctx.clearRect(0, 0, width, height);

  const pts: { x: number; y: number }[] = [];

  // Include last pivot before visible range for line continuity
  for (let i = startIdx - 1; i >= 0; i--) {
    if (values[i] !== null) { pts.push({ x: getX(i), y: priceToY(values[i]!) }); break; }
  }
  for (let i = startIdx; i <= endIdx; i++) {
    if (values[i] !== null) pts.push({ x: getX(i), y: priceToY(values[i]!) });
  }
  // Include first pivot after visible range
  for (let i = endIdx + 1; i < values.length; i++) {
    if (values[i] !== null) { pts.push({ x: getX(i), y: priceToY(values[i]!) }); break; }
  }

  if (pts.length < 2) return;

  ctx.strokeStyle = settings.mainLine.color;
  ctx.lineWidth = settings.mainLine.width;
  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
  ctx.stroke();
}
